import { useState } from 'react';
import { useNotifications } from 'src/contexts/NotificationContext';
import { useNotificationTracking } from './useNotificationTracking';

interface BulkActionState {
  selectedNotifications: Set<number>;
  bulkActionLoading: boolean;
}

export const useNotificationBulkActions = (): {
  bulkActionState: BulkActionState;
  handleSelectNotification: (notifId: number, selected?: boolean) => void;
  handleBulkMarkAsRead: () => Promise<void>;
  handleBulkMarkAsUnread: () => Promise<void>;
  clearSelection: () => void;
} => {
  const { bulkMarkAsRead, bulkMarkAsUnread } = useNotifications();
  const { trackNotificationSelect, trackNotificationBulkAction } =
    useNotificationTracking();

  const [selectedNotifications, setSelectedNotifications] = useState(
    new Set<number>()
  );
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const handleSelectNotification = (
    notifId: number,
    selected?: boolean
  ): void => {
    setSelectedNotifications((prev) => {
      const newSet = new Set(prev);
      const wasSelected = newSet.has(notifId);

      // Use the selected parameter if provided, otherwise toggle
      const shouldBeSelected = selected !== undefined ? selected : !wasSelected;

      if (shouldBeSelected) {
        newSet.add(notifId);
      } else {
        newSet.delete(notifId);
      }

      // Track selection action
      trackNotificationSelect(notifId, shouldBeSelected, {
        selected_count: newSet.size,
        debug_info: {
          component_name: 'useNotificationBulkActions',
          function_name: 'handleSelectNotification',
        },
      });

      return newSet;
    });
  };

  const handleBulkMarkAsRead = async (): Promise<void> => {
    if (selectedNotifications.size === 0) {
      return;
    }

    const notifIds = Array.from(selectedNotifications);

    setBulkActionLoading(true);
    try {
      await bulkMarkAsRead(notifIds);

      // Track bulk mark as read action
      trackNotificationBulkAction('mark_read', notifIds.length, {
        selected_count: notifIds.length,
        debug_info: {
          component_name: 'useNotificationBulkActions',
          function_name: 'handleBulkMarkAsRead',
        },
      });

      setSelectedNotifications(new Set());
    } catch (error) {
      // Error handling is done in the context
      console.error('Bulk mark as read failed:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkMarkAsUnread = async (): Promise<void> => {
    if (selectedNotifications.size === 0) {
      return;
    }

    const notifIds = Array.from(selectedNotifications);

    setBulkActionLoading(true);
    try {
      await bulkMarkAsUnread(notifIds);

      // Track bulk mark as unread action
      trackNotificationBulkAction('mark_unread', notifIds.length, {
        selected_count: notifIds.length,
        debug_info: {
          component_name: 'useNotificationBulkActions',
          function_name: 'handleBulkMarkAsUnread',
        },
      });

      setSelectedNotifications(new Set());
    } catch (error) {
      // Error handling is done in the context
      console.error('Bulk mark as unread failed:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const clearSelection = (): void => {
    setSelectedNotifications(new Set());
  };

  const bulkActionState: BulkActionState = {
    selectedNotifications,
    bulkActionLoading,
  };

  return {
    bulkActionState,
    handleSelectNotification,
    handleBulkMarkAsRead,
    handleBulkMarkAsUnread,
    clearSelection,
  };
};
